import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, switchMap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  JoinOrganizationRequestDto,
  JoinOrganizationResponseDto,
  OrganizationRequestDto,
  OrganizationResponseDto,
  OrgTypesListResponseDto,
  CollaborativeOnboardingRequestDto,
  CollaborativeOnboardingResponseDto,
  DiscoveryResponseDto,
  JoinWorkspaceResponseDto,
  AcceptInvitationResponseDto,
  SendOrgInvitationsResponseDto,
  OrgInviteRowDto,
  PresignedUploadRequestDto,
  PresignedUploadResponseDto,
  UpdateUserProfileDto,
  UpdateUserProfileResponseDto,
} from '../models/organization.dto';
import { ApiResponse } from 'src/app/shared/models/api-response.dto';
import { UserProfileSetupDto } from '../../auth/models/signup.dto';
import { SessionService } from 'src/app/shared/services/session.service';
import {
  JoinableProjectsResponseDto,
  ProjectBulkJoinResponseDto,
  ProjectBulkJoinUndoResponseDto,
  ProjectJoinResponseDto,
} from '../models/joinable-project.dto';
import { ProjectResponseDto } from '../../dashboard/models/project.dto';

/** The most distinct projects `POST /api/projects/join-bulk/` takes in one request. */
export const MAX_BULK_JOIN_PROJECTS = 200;

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private readonly apiUrl = `${environment.apiHost}/api`;

  private readonly session = inject(SessionService);
  constructor(private readonly http: HttpClient) {}

  // The HTTP interceptor will automatically add the authorization token
  createOrganization(organization: OrganizationRequestDto): Observable<OrganizationResponseDto> {
    return this.http.post<OrganizationResponseDto>(`${this.apiUrl}/organizations/`, organization);
  }

  joinOrganization(payload: JoinOrganizationRequestDto): Observable<JoinOrganizationResponseDto> {
    return this.http.post<JoinOrganizationResponseDto>(`${this.apiUrl}/organizations/join/`, payload);
  }

  /** Fetch available organization types. GET /api/organization-types/ */
  getOrganizationTypes(): Observable<OrgTypesListResponseDto> {
    return this.http.get<OrgTypesListResponseDto>(`${this.apiUrl}/organization-types/`);
  }

  /**
   * Complete the collaborative client onboarding in a single atomic call.
   * The backend validates the JWT, creates the org, links it to the agency, and
   * activates the feature flags encoded in the invitation token.
   * POST /api/organizations/collaborative-onboarding/
   */
  collaborativeOnboarding(body: CollaborativeOnboardingRequestDto): Observable<CollaborativeOnboardingResponseDto> {
    return this.http.post<CollaborativeOnboardingResponseDto>(
      `${this.apiUrl}/organizations/collaborative-onboarding/`,
      body,
    );
  }

  // ─── Company-discovery onboarding ────────────────────────────────────────────

  /** Workspaces the signed-in user can join (domain matches + pending invites). */
  getDiscovery(): Observable<DiscoveryResponseDto> {
    return this.http.get<DiscoveryResponseDto>(`${this.apiUrl}/organizations/discovery/`);
  }

  /** Join a domain-matched workspace; may return an immediate join or a pending approval. */
  joinDomainWorkspace(organizationId: string): Observable<JoinWorkspaceResponseDto> {
    return this.http.post<JoinWorkspaceResponseDto>(`${this.apiUrl}/organizations/${organizationId}/join-domain/`, {});
  }

  /** Accept an invitation addressed to the signed-in user. */
  acceptInvitation(invitationId: string): Observable<AcceptInvitationResponseDto> {
    return this.http.post<AcceptInvitationResponseDto>(
      `${this.apiUrl}/organizations/invitations/${invitationId}/accept/`,
      {},
    );
  }

  /** Decline an invitation addressed to the signed-in user. */
  declineInvitation(invitationId: string): Observable<AcceptInvitationResponseDto> {
    return this.http.post<AcceptInvitationResponseDto>(
      `${this.apiUrl}/organizations/invitations/${invitationId}/decline/`,
      {},
    );
  }

  /** Create & send organization invitations. POST /api/organizations/{id}/invitations/ */
  sendOrgInvitations(organizationId: string, invites: OrgInviteRowDto[]): Observable<SendOrgInvitationsResponseDto> {
    return this.http.post<SendOrgInvitationsResponseDto>(
      `${this.apiUrl}/organizations/${organizationId}/invitations/`,
      { invites },
    );
  }

  // ─── First-time setup wizard ─────────────────────────────────────────────────

  /** Get a presigned R2 upload URL (reuses the attachments endpoint; no ticket needed). */
  getPresignedUploadUrl(body: PresignedUploadRequestDto): Observable<PresignedUploadResponseDto> {
    return this.http.post<PresignedUploadResponseDto>(`${this.apiUrl}/attachments/presigned-url/`, body);
  }

  /** Upload a file directly to R2 via the presigned URL (no auth header — URL is signed). */
  uploadFileToPresignedUrl(uploadUrl: string, file: File): Observable<void> {
    return this.http.put<void>(uploadUrl, file, {
      headers: { 'Content-Type': file.type },
    });
  }

  /**
   * Presign under `scope`, then PUT the file. Emits the object key — the thing to save on the owning
   * resource. The upload response's `public_url` is legacy and may be empty; read URLs are signed
   * and expire, so they are never what gets persisted.
   */
  uploadFile(file: File, scope: Omit<PresignedUploadRequestDto, 'filename' | 'content_type'>): Observable<string> {
    return this.getPresignedUploadUrl({ filename: file.name, content_type: file.type, ...scope }).pipe(
      switchMap((resp) => {
        if (resp.status !== 'success' || !resp.data) {
          return throwError(() => new Error(resp.message || 'Failed to get upload URL.'));
        }
        const { upload_url, key } = resp.data;
        return this.uploadFileToPresignedUrl(upload_url, file).pipe(map(() => key));
      }),
    );
  }

  /** Upload a workspace logo and save its key. The key is scoped to the organization, so it must exist. */
  uploadWorkspaceLogo(file: File, organizationId: string): Observable<OrganizationResponseDto> {
    return this.uploadFile(file, { prefix: 'workspaces', organization_id: organizationId }).pipe(
      switchMap((key) =>
        this.http.patch<OrganizationResponseDto>(`${this.apiUrl}/organizations/${organizationId}/`, { logo_key: key }),
      ),
    );
  }

  /** Upload a project logo and save its key. The key is scoped to the project, so it must exist. */
  uploadProjectLogo(file: File, projectId: string): Observable<ProjectResponseDto> {
    return this.uploadFile(file, { prefix: 'projects', project_id: projectId }).pipe(
      switchMap((key) =>
        this.http.patch<ProjectResponseDto>(`${this.apiUrl}/projects/${projectId}/`, { logo_key: key }),
      ),
    );
  }

  /** Update the signed-in user's profile (name + avatar). PATCH /api/users/{id}/ */
  updateUserProfile(userId: string, body: UpdateUserProfileDto): Observable<UpdateUserProfileResponseDto> {
    return this.http.patch<UpdateUserProfileResponseDto>(`${this.apiUrl}/users/${userId}/`, body);
  }

  /** Fetch the signed-in user's profile (email + name for prefill). GET /api/users/{id}/ */
  getUser(userId: string): Observable<UpdateUserProfileResponseDto> {
    return this.http.get<UpdateUserProfileResponseDto>(`${this.apiUrl}/users/${userId}/`);
  }

  public sendProfileSetup(profileSetupDto: UserProfileSetupDto): Observable<ApiResponse> {
    return this.http.patch<ApiResponse>(
      `${this.apiUrl}/users/${this.session.profile()!.id}?reason=onboarding`,
      profileSetupDto,
    );
  }

  // ─── The join-projects step ──────────────────────────────────────────────────
  // Only the first of these is deployed; the rest are what the step still needs.

  /**
   * The organization's projects, each carrying whether this user may join it outright and how
   * far any request of theirs has got.
   *
   * GET /api/organizations/{organization_id}/projects-onboarding/
   */
  getOrganizationProjects(organizationId: string): Observable<JoinableProjectsResponseDto> {
    return this.http.get<JoinableProjectsResponseDto>(
      `${this.apiUrl}/organizations/${organizationId}/projects-onboarding/`,
    );
  }

  /** Join a project that is open to this user. POST /api/projects/{project_id}/join/ */
  joinProjectById(projectId: string): Observable<ProjectJoinResponseDto> {
    return this.http.post<ProjectJoinResponseDto>(`${this.apiUrl}/projects/${projectId}/join/`, {});
  }

  /**
   * Join several projects in one go. The server decides each one as the single calls would —
   * open ones joined, restricted ones requested — and reports every project separately, so one
   * refusal does not sink the rest.
   *
   * At most `MAX_BULK_JOIN_PROJECTS` distinct ids per request; past that the server refuses the
   * whole request with a 400 whose `errors.project_ids` says why.
   *
   * POST /api/projects/join-bulk/
   */
  joinProjectsBulk(projectIds: readonly string[]): Observable<ProjectBulkJoinResponseDto> {
    return this.http.post<ProjectBulkJoinResponseDto>(`${this.apiUrl}/projects/join-bulk/`, {
      project_ids: projectIds,
    });
  }

  /**
   * Takes back what one bulk join did: the memberships it created, the requests it filed, or
   * both, named by the keys that call returned.
   *
   * Addressed by key rather than by project, so an undo can only reach what that one call did —
   * a project the user was already in, or asked for earlier, is not swept up by it.
   *
   * POST /api/projects/join-bulk/undo/
   */
  undoBulkJoin(keys: {
    bulkJoinKey?: string | null;
    bulkRequestKey?: string | null;
  }): Observable<ProjectBulkJoinUndoResponseDto> {
    return this.http.post<ProjectBulkJoinUndoResponseDto>(`${this.apiUrl}/projects/join-bulk/undo/`, {
      ...(keys.bulkJoinKey ? { bulk_join_key: keys.bulkJoinKey } : {}),
      ...(keys.bulkRequestKey ? { bulk_request_key: keys.bulkRequestKey } : {}),
    });
  }

  /** Ask a closed project's admins to let this user in. POST /api/projects/{project_id}/join-request/ */
  requestProjectAccess(projectId: string): Observable<ProjectJoinResponseDto> {
    return this.http.post<ProjectJoinResponseDto>(`${this.apiUrl}/projects/${projectId}/join-request/`, {});
  }

  /**
   * Withdraw this user's pending request for a project.
   *
   * Addressed by project rather than by request id: the list this is called from carries the
   * project, and the server is the only side that knows which request of the caller's it is.
   *
   * POST /api/projects/{project_id}/join-request/cancel/
   */
  cancelProjectJoinRequest(projectId: string): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/projects/${projectId}/join-request/cancel/`, {});
  }

  /**
   * Leave the join-project step behind, whatever the user did on it. The server is what moves
   * them to `profile_setup`, which is why the caller reloads the session rather than assuming.
   *
   * POST /api/users/onboarding/join-project/complete/
   */
  completeJoinProjectStep(): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.apiUrl}/users/onboarding/join-project/complete/`, {});
  }
}
